import type {
  Match,
  PadelMatchResult,
  PickleballMatchResult,
  Sport,
  SportMatchResult,
  Team,
} from "@/types";
import { detectPadelWinner, padelMatchResultToString } from "./padel";
import {
  detectPickleballWinner,
  pickleballMatchResultToString,
} from "./pickleball";

export { getSportIcon } from "./icons";
export {
  detectPadelWinner,
  padelMatchResultToString,
} from "./padel";
export {
  detectPickleballGameWinner,
  detectPickleballWinner,
  isCompletePickleballGame,
  pickleballMatchResultToString,
} from "./pickleball";

/**
 * Detect the winning team id for a match based on its sport.
 */
export function detectWinner(
  match: Match,
  result: SportMatchResult,
): Team["id"] | null {
  switch (match.sport) {
    case "padel":
      return detectPadelWinner(match, result as PadelMatchResult);
    case "pickleball":
      return detectPickleballWinner(match, result as PickleballMatchResult);
    default:
      return null;
  }
}

/**
 * Format a match result as a human-readable string.
 */
export function matchResultToString(
  result: SportMatchResult,
  sport: Sport["slug"],
): string {
  switch (sport) {
    case "padel":
      return padelMatchResultToString(result as PadelMatchResult);
    case "pickleball":
      return pickleballMatchResultToString(result as PickleballMatchResult);
    default:
      return "Result not available";
  }
}

/**
 * Build a zeroed match result document for a walkover.
 * Centralised so the three places that record walkovers (createChallenge
 * forfeit path, submitWalkover, adminUpdateMatch) stay consistent.
 */
export function buildWalkoverResult(
  match: Pick<Match, "id" | "tournamentId" | "sport">,
  winnerId: Team["id"],
  resultId?: string,
): SportMatchResult {
  const now = new Date();
  const id = resultId ?? match.id;

  if (match.sport === "pickleball") {
    const r: PickleballMatchResult = {
      id,
      matchId: match.id,
      tournamentId: match.tournamentId,
      winnerId,
      walkover: true,
      createdAt: now,
      updatedAt: now,
      sport: "pickleball",
      games: [],
    };
    return r;
  }

  // Default to padel-shape (covers "padel" and any unknown legacy sport).
  const r: PadelMatchResult = {
    id,
    matchId: match.id,
    tournamentId: match.tournamentId,
    winnerId,
    walkover: true,
    createdAt: now,
    updatedAt: now,
    sport: match.sport,
    set1: { team1Games: 0, team2Games: 0 },
    set2: { team1Games: 0, team2Games: 0 },
    set3: { team1Games: 0, team2Games: 0 },
  };
  return r;
}
